import { createSlice } from '@reduxjs/toolkit';

import type { UserGroups } from '@/src/components/ListTable/type/ListTableType';
import { PREV_SELECTED_FOLDER } from '@/src/modules/knowledge-base/ContentSource/constants';
import { ALL_FILES } from '@/src/shared/constants/KnowledgeBaseConstants';
import type {
    Folder,
    LegalEntity,
} from '@/src/shared/services/apiServices/responseModel/kbFolderListType';

import { applyStateupdate } from '../utils';

interface KbSlice {
    'state/initialLoad': boolean;
    'state/loading': boolean;
    'state/dirty': boolean;
    'state/folderNames': string[];
    'state/folderList': Folder[];
    'state/selectedFolder': string | null;
    'state/isFolderDeletable': boolean;
    // new file upload
    'state/global': boolean;
    'state/globalUserGroups': boolean;
    'state/legalEntity': LegalEntity[];
    'state/userGroup': UserGroups[];
    'state/preselectFirstFolder': boolean | null;
    'state/sourceType': string;
    'state/selectedSharepointFiles': any[];
    'state/selectedDriveId': any[];
    'state/selectedFolderId': string;
    'state/siteId': string;
    'state/refreshFolderList': boolean;
    'state/showSyncNotification': boolean;
    'state/uploadingFile': boolean;
    'state/isLibraryOpened': boolean;
    'state/libraryDetail': object;
    'state/allFilesCount': number;
}

export const initState: KbSlice = {
    'state/initialLoad': true,
    'state/loading': false,
    'state/dirty': false,
    'state/folderNames': [],
    'state/folderList': [],
    'state/selectedFolder': null,
    'state/isFolderDeletable': false,
    'state/global': true,
    'state/globalUserGroups': true,
    'state/legalEntity': [],
    'state/userGroup': [],
    'state/preselectFirstFolder': false,
    'state/sourceType': '',
    'state/selectedSharepointFiles': [],
    'state/selectedDriveId': [],
    'state/selectedFolderId': '',
    'state/siteId': '',
    'state/refreshFolderList': false,
    'state/showSyncNotification': false,
    'state/uploadingFile': false,
    'state/isLibraryOpened': false,
    'state/libraryDetail': {},
    'state/allFilesCount': 0,
};

export const kbSlice = createSlice({
    name: 'state',
    initialState: initState,

    reducers: {
        folderList: (state, action) => {
            const newState = applyStateupdate(state, action);
            const folders = newState['state/folderList'];
            const folderNames = folders
                .map((folder: Folder) => folder.name)
                .sort((a: string, b: string) => a.localeCompare(b));
            newState['state/folderNames'] = folderNames;
            const selectedFolder = newState['state/selectedFolder'];
            if (!folders.find((folder: Folder) => folder.name === selectedFolder)) {
                newState['state/selectedFolder'] = null;
            }
            if (newState['state/preselectFirstFolder'] && folderNames.length) {
                newState['state/selectedFolder'] = ALL_FILES;
            }
            newState['state/preselectFirstFolder'] = false;
            return newState;
        },
        updateFolderState: (state, action) => {
            const { isLibraryOpened, libraryDetail, selectedFolder } = action.payload;
            const newState = { ...state };
            newState['state/isLibraryOpened'] = isLibraryOpened;
            newState['state/libraryDetail'] = libraryDetail;
            newState['state/selectedFolder'] = selectedFolder;
            newState['state/global'] = true;
            newState['state/legalEntity'] = [];
            return newState;
        },
        selectedFolder: (state, action) => {
            const newState = applyStateupdate(state, action);
            newState['state/global'] = true;
            newState['state/legalEntity'] = [];
            return newState;
        },
        selectFirstFolder: (state) => {
            const newState = state;
            const prevSelectedFolder = sessionStorage.getItem(PREV_SELECTED_FOLDER);
            const firstFolder = prevSelectedFolder || ALL_FILES || state['state/folderNames'][0];
            newState['state/selectedFolder'] = firstFolder || null;
            newState['state/global'] = true;
            newState['state/legalEntity'] = [];
            return newState;
        },
        preSelectFirstFolder: (state) => {
            const newState = state;
            newState['state/preselectFirstFolder'] = true;
            return newState;
        },
        dirty: (state, action) => {
            return applyStateupdate(state, action);
        },
        initialLoad: (state, action) => {
            return applyStateupdate(state, action);
        },
        loading: (state, action) => {
            return applyStateupdate(state, action);
        },
        global: (state, action) => {
            return applyStateupdate(state, action);
        },
        globalUserGroups: (state, action) => {
            return applyStateupdate(state, action);
        },
        legalEntity: (state, action) => {
            return applyStateupdate(state, action);
        },
        userGroup: (state, action) => {
            return applyStateupdate(state, action);
        },
        sourceType: (state, action) => {
            return applyStateupdate(state, action);
        },
        resetState: () => {
            return initState;
        },
        selectedSharepointFiles: (state, action) => {
            return applyStateupdate(state, action);
        },
        selectedDriveId: (state, action) => {
            return applyStateupdate(state, action);
        },
        selectedFolderId: (state, action) => {
            return applyStateupdate(state, action);
        },
        isFolderDeletable: (state, action) => {
            return applyStateupdate(state, action);
        },
        siteId: (state, action) => {
            return applyStateupdate(state, action);
        },
        refreshFolderList: (state, action) => {
            return applyStateupdate(state, action);
        },
        showSyncNotification: (state, action) => {
            return applyStateupdate(state, action);
        },
        uploadingFile: (state, action) => {
            return applyStateupdate(state, action);
        },
        isLibraryOpened: (state, action) => {
            return applyStateupdate(state, action);
        },
        libraryDetail: (state, action) => {
            return applyStateupdate(state, action);
        },
        allFilesCount: (state, action) => {
            return applyStateupdate(state, action);
        },
    },
});

export default kbSlice;
